import React, { useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { TFunction } from "i18next/typescript/t";

import FilterRenderer from "@/components_v2/Filters/FilterRenderer";
import { FilterField } from "@/components_v2/Filters/types";
import {
  HookFormInfiniteScrollDropdown,
  HookFormInput,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import {
  useLazyGetAchievementsQuery,
  useLazyGetAreasOfExpertiseQuery,
  useLazyGetCommunityRolesQuery,
  useLazyGetCountriesQuery,
  useLazyGetOrganizationsQuery,
  useLazyGetProfileTypesQuery,
} from "@/redux/service";
import {
  CountryResponse,
  CountryQueryParams,
  AchievementResponse,
  AchievementQueryParams,
  CommunityRoleResponse,
  CommunityRoleQueryParams,
} from "@/redux/common/types";
import {
  UserFiltersProps,
  UserFiltersState,
} from "@/features/users/list/types";
import {
  BaseOrganizationResponse,
  OrganizationQueryParams,
} from "@/redux/organizations/types";

import { useFilterCount } from "@/shared/hooks/useFilterCount";

import {
  ExpertiseAreaQueryParams,
  ExpertiseAreaResponse,
  ProfileTypeQueryParams,
  ProfileTypeResponse,
} from "@/redux/users/types";

const userFiltersSchema = (
  t: TFunction,
): yup.ObjectSchema<Omit<UserFiltersState, "role">> =>
  yup
    .object()
    .shape({
      search: yup
        .string()
        .transform((value) => (value === "" ? undefined : value))
        .typeError(t("form.validation.notValid")),
      organizations: yup
        .array()
        .of(yup.number().required().typeError(t("form.validation.notValid"))),
      countries: yup
        .array()
        .of(yup.number().required().typeError(t("form.validation.notValid"))),
      achievements: yup
        .array()
        .of(yup.number().required().typeError(t("form.validation.notValid"))),
      communityRoles: yup
        .array()
        .of(yup.number().required().typeError(t("form.validation.notValid"))),
      expertiseAreas: yup
        .array()
        .of(yup.number().required().typeError(t("form.validation.notValid"))),
      exclude: yup
        .array()
        .of(yup.number().required().typeError(t("form.validation.notValid"))),
      department: yup.string().typeError(t("form.validation.notValid")),
      jobTitle: yup.string().typeError(t("form.validation.notValid")),
      excludeInvitedToSurvey: yup
        .number()
        .typeError(t("form.validation.notValid")),
      profileTypes: yup
        .array()
        .of(yup.number().required().typeError(t("form.validation.notValid"))),
    })
    .required();

const UserFilters = ({
  filterNames,
  setFilters,
  defaultFilters,
}: UserFiltersProps) => {
  const { t } = useTranslation();

  const [getCountries] = useLazyGetCountriesQuery();
  const [getOrganizations] = useLazyGetOrganizationsQuery();
  const [getAchievements] = useLazyGetAchievementsQuery();
  const [getCommunityRoles] = useLazyGetCommunityRolesQuery();
  const [getExpertiseAreas] = useLazyGetAreasOfExpertiseQuery();
  const [getProfileTypes] = useLazyGetProfileTypesQuery();

  const form = useForm<UserFiltersState>({
    resolver: yupResolver(userFiltersSchema(t)),
    mode: "onChange",
    defaultValues: {
      search: undefined,
      organizations: undefined,
      countries: [],
      achievements: [],
      communityRoles: [],
      expertiseAreas: [],
      department: undefined,
      jobTitle: undefined,
      exclude: [],
      profileTypes: [],
      ...defaultFilters,
    },
  });

  const filterFields: Record<string, FilterField> = useMemo(() => {
    return {
      search: {
        name: "search",
        isAdvancedFilter: false,
        isDebounced: true,
        component: (
          <HookFormInput
            name="search"
            control={form.control}
            suffix={<SearchOutlined />}
            placeholder={t("users.placeholders.searchUsers")}
          />
        ),
      },
      organizations: {
        name: "organizations",
        label: t("users.labels.organizations"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormInfiniteScrollDropdown<
            BaseOrganizationResponse,
            OrganizationQueryParams
          >
            name="organizations"
            control={form.control}
            fetchFn={getOrganizations}
            labelFieldName="name"
            valueFieldName="id"
            lookupFieldName="ids"
            searchFieldName="search"
            multiple
            placeholder={t("users.placeholders.selectOrganizations")}
          />
        ),
      },
      countries: {
        name: "countries",
        label: t("users.labels.countries"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormInfiniteScrollDropdown<CountryResponse, CountryQueryParams>
            name="countries"
            control={form.control}
            fetchFn={getCountries}
            labelFieldName="name"
            valueFieldName="id"
            lookupFieldName="ids"
            searchFieldName="name"
            multiple
            placeholder={t("users.placeholders.selectCountries")}
          />
        ),
      },
      achievements: {
        name: "achievements",
        label: t("users.labels.achievements"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormInfiniteScrollDropdown<
            AchievementResponse,
            AchievementQueryParams
          >
            name="achievements"
            control={form.control}
            fetchFn={getAchievements}
            labelFieldName="label"
            valueFieldName="id"
            lookupFieldName="ids"
            searchFieldName="label"
            multiple
            placeholder={t("users.placeholders.selectAchievements")}
            defaultFilters={{
              isAssigned: true,
              type: "profile",
            }}
          />
        ),
      },
      communityRoles: {
        name: "communityRoles",
        label: t("users.labels.communityRoles"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormInfiniteScrollDropdown<
            CommunityRoleResponse,
            CommunityRoleQueryParams
          >
            name="communityRoles"
            control={form.control}
            fetchFn={getCommunityRoles}
            labelFieldName="label"
            valueFieldName="id"
            lookupFieldName="ids"
            searchFieldName="label"
            multiple
            placeholder={t("users.placeholders.selectCommunityRoles")}
            defaultFilters={{
              isAssigned: true,
              type: "profile",
            }}
          />
        ),
      },
      expertiseAreas: {
        name: "expertiseAreas",
        label: t("users.labels.expertiseAreas"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormInfiniteScrollDropdown<
            ExpertiseAreaResponse,
            ExpertiseAreaQueryParams
          >
            name="expertiseAreas"
            control={form.control}
            fetchFn={getExpertiseAreas}
            labelFieldName="name"
            valueFieldName="id"
            lookupFieldName="ids"
            searchFieldName="name"
            multiple
            placeholder={t("users.placeholders.selectExpertiseAreas")}
            defaultFilters={{
              isAssigned: true,
              type: "profile",
            }}
          />
        ),
      },
      department: {
        name: "department",
        label: t("users.labels.department"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormInput
            name="department"
            control={form.control}
            placeholder={t("users.placeholders.enterDepartment")}
          />
        ),
      },
      jobTitle: {
        name: "jobTitle",
        label: t("users.labels.jobTitle"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormInput
            name="jobTitle"
            control={form.control}
            placeholder={t("users.placeholders.enterJobTitle")}
          />
        ),
      },
      profileTypes: {
        name: "profileTypes",
        label: t("users.labels.profileTypes"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormInfiniteScrollDropdown<
            ProfileTypeResponse,
            ProfileTypeQueryParams
          >
            name="profileTypes"
            control={form.control}
            fetchFn={getProfileTypes}
            labelFieldName="name"
            valueFieldName="id"
            lookupFieldName="ids"
            searchFieldName="name"
            multiple
            placeholder={t("users.placeholders.selectProfileTypes")}
            allowClear
            defaultFilters={{
              isDefault: false,
            }}
          />
        ),
      },
    };
  }, [
    form.control,
    t,
    getCountries,
    getOrganizations,
    getAchievements,
    getCommunityRoles,
    getExpertiseAreas,
    getProfileTypes,
  ]);

  const activeFiltersCount = useFilterCount<UserFiltersState>(
    form,
    defaultFilters,
  );

  return (
    <FilterRenderer
      form={form}
      filters={filterFields}
      activeFilterNames={filterNames}
      popoverTitle={t("users.labels.filters")}
      handleClear={() => {
        form.reset(defaultFilters);
        setFilters(form.getValues());
      }}
      onFiltersChange={setFilters}
      areFiltersDirty={form.formState.isDirty}
      activeFilterCount={activeFiltersCount}
      handleSearch={() => setFilters(form.getValues())}
      className="filter-layout"
    />
  );
};

export default UserFilters;
